import { Modal } from 'antd';
import React from 'react';
import ewtStore from '../EWTStore';
import { ewtModalClass } from '../theme';
import { LabeledInput, LabeledSelect } from './LabeledFormControls';

const EMPTY_SLOTS = [null, null, null, null];

const playerLabel = (player) => {
    if (!player) return '';
    const shortName = player.shortName || `${player.first_name} ${player.last_name}`;
    return `${shortName} (${player.id})`;
};

const sortPlayersByName = (players) =>
    [...players].sort((a, b) => {
        const last = (a.last_name || '').localeCompare(b.last_name || '');
        if (last !== 0) return last;
        return (a.first_name || '').localeCompare(b.first_name || '');
    });

const isPlayerActive = (player) => player.active !== false;

const getSelectablePlayers = (players, slots) => {
    const slottedIds = new Set(slots.filter(Boolean));
    return sortPlayersByName(
        (players || []).filter(
            (player) => isPlayerActive(player) || slottedIds.has(player.id)
        )
    );
};

const buildPlayerOptions = (players, slotIndex, slots) =>
    players.map((player) => ({
        value: player.id,
        label: playerLabel(player),
        disabled: slots.some((id, index) => index !== slotIndex && id === player.id),
    }));

export const DataGroupEdit = (props) => {
    const [group, setGroup] = React.useState(null);
    const [slots, setSlots] = React.useState(EMPTY_SLOTS);

    React.useEffect(() => {
        if (!props.open || !props.group) return;
        const groupCopy = JSON.parse(JSON.stringify(props.group));
        setGroup(groupCopy);
        const roundPlayers = props.round && props.round.players ? props.round.players : {};
        const assigned = Object.keys(roundPlayers)
            .filter((playerID) => roundPlayers[playerID].groupID === props.group.id)
            .slice(0, 4);
        const nextSlots = [...EMPTY_SLOTS];
        assigned.forEach((playerID, index) => {
            nextSlots[index] = playerID;
        });
        setSlots(nextSlots);
    }, [props.open, props.group, props.round]);

    const save = async () => {
        if (!props.roundID || !group) return;
        await ewtStore.saveRoundGroup(
            props.roundID,
            group.id,
            {
                groupNumber: group.groupNumber,
                teeTime: group.teeTime,
                startingHole: group.startingHole,
            },
            slots
        );
        props.hideModal();
    };

    const setSlot = (index, playerID) => {
        const nextSlots = [...slots];
        if (playerID) {
            for (let i = 0; i < nextSlots.length; i++) {
                if (i !== index && nextSlots[i] === playerID) nextSlots[i] = null;
            }
        }
        nextSlots[index] = playerID || null;
        setSlots(nextSlots);
    };

    const selectablePlayers = React.useMemo(
        () => getSelectablePlayers(props.players, slots),
        [props.players, slots]
    );

    return (
        <Modal
            className={ewtModalClass}
            title={`Group ${group ? group.groupNumber : ''}`}
            open={props.open}
            onOk={save}
            onCancel={props.hideModal}
            okText="Save"
            cancelText="Cancel"
        >
            {group && (
                <div className="flexColumn ewtLabeledForm ewtLabeledForm--group" style={{ gap: 8 }}>
                    <LabeledInput
                        label="Group #"
                        value={group.groupNumber}
                        onChange={(e) =>
                            setGroup({ ...group, groupNumber: e.target.value })
                        }
                    />
                    <LabeledInput
                        label="Tee Time"
                        value={group.teeTime}
                        onChange={(e) =>
                            setGroup({ ...group, teeTime: e.target.value })
                        }
                    />
                    <LabeledInput
                        label="Starting Hole"
                        value={group.startingHole || '1'}
                        onChange={(e) =>
                            setGroup({ ...group, startingHole: e.target.value })
                        }
                    />
                    {slots.map((slotPlayerID, index) => (
                        <LabeledSelect
                            key={index}
                            label={`Player ${index + 1}`}
                            value={slotPlayerID || undefined}
                            onChange={(value) => setSlot(index, value)}
                            allowClear
                            placeholder="Select player"
                            showSearch
                            optionFilterProp="label"
                            options={buildPlayerOptions(selectablePlayers, index, slots)}
                        />
                    ))}
                </div>
            )}
        </Modal>
    );
};
